/**
 * `PreviewModule` — the live-preview "view" extracted out of `Workspace.tsx`
 * (design doc `live-preview-side-by-side.md` §4 PR B).
 *
 * A presentational component that owns the ~85-line PreviewOverlay
 * composition (the `overrideActive` derivation, the conditional
 * `pairing` / `previewMode` / `onAddPhone` / `onRemovePhone` /
 * `onRemovePhoneAt` wiring, the `onSendErrorToChat` envelope). It consumes
 * the `usePreviewModule` hook object (the "brain", PR A) plus the small set
 * of Workspace-owned, chat-side inputs the overlay needs (`workspaceMode`,
 * `gameName`, `newUi`, the selection-to-chat handler, the toast setter, the
 * chat dispatcher). Absorbing `PreviewOverlay`'s prop surface in ONE place
 * shields `Workspace` from it.
 *
 * BEHAVIOUR IS PRESERVED EXACTLY: this is a verbatim lift of the former
 * `renderPreviewSurface` closure body, so the render output (and therefore
 * the render-tree position / no-remount guarantee) is unchanged. It is a
 * component (not a `render*` closure) but is still mounted from the same
 * three sites in `Workspace` (the full-bleed Preview tab + the two
 * north-star override cases), so React sees the same element identity in
 * each slot.
 */
import { PREVIEW_PHASE_GATE_ERROR_CODE } from '@volley/crucible-ipc-types/preview-gate-constants';
import { PreviewOverlay } from '../components/PreviewOverlay';
import { HARD_PREVIEW_PHONE_CAP } from '../workspace/preview-controller-policy';
import type { ChatAction } from '../chat/state';
import type { PreviewToast, UsePreviewModule } from './usePreviewModule';

const NOOP = (): void => {};

/** Steering-surface overrides — when any is set, the overlay renders the
 *  override node(s) in place of the live TV/phone panes and suppresses the
 *  live lifecycle affordances (matches the former `overrideActive` gate). */
export interface PreviewModuleOverrides {
  displayOverride?: React.ReactNode;
  controllerOverride?: React.ReactNode;
  controllerPanelOverride?: React.ReactNode;
}

export interface PreviewModuleProps {
  /** The preview "brain" from `usePreviewModule`. */
  preview: UsePreviewModule;
  /** Current workspace mode — drives the selection fly-to target. */
  workspaceMode: string;
  gameName: string;
  newUi: boolean;
  overrides?: PreviewModuleOverrides;
  /** Attach a TV selection to the chat composer. */
  onAddSelectionToChat: (payload: { file: File; contextBlock: string }) => void;
  /** Toast setter (selection errors surface here). */
  setPreviewToast: (toast: PreviewToast | null) => void;
  /** Chat dispatcher (the "send error to chat" affordance). */
  chatDispatch: React.Dispatch<ChatAction>;
}

/**
 * The preview view. See file-level doc — a verbatim lift of the former
 * `renderPreviewSurface` composition, behaviour-preserving.
 */
export function PreviewModule({
  preview,
  workspaceMode,
  gameName,
  newUi,
  overrides,
  onAddSelectionToChat,
  setPreviewToast,
  chatDispatch,
}: PreviewModuleProps): React.JSX.Element {
  const overrideActive = Boolean(
    overrides?.displayOverride ||
    overrides?.controllerOverride ||
    overrides?.controllerPanelOverride,
  );
  return (
    <PreviewOverlay
      embedded
      // Full Preview mode has no chat panel on screen → fly to the Develop tab.
      // In-editor (Develop split) → fly to the composer thumbnail.
      flyTo={workspaceMode === 'preview' ? 'chat-tab' : 'composer'}
      newUi={newUi}
      gameName={gameName}
      onClose={NOOP}
      displayUrl={overrideActive ? undefined : preview.derived.displayUrl}
      displayOverride={overrides?.displayOverride}
      displayIframeRef={overrideActive ? undefined : preview.bridge.displayIframeRef}
      onDisplayIframeLoad={overrideActive ? undefined : preview.bridge.onDisplayIframeLoad}
      onAddSelectionToChat={overrideActive ? undefined : onAddSelectionToChat}
      onSelectionError={(message) => setPreviewToast({ tone: 'error', message })}
      // Lifecycle controls now live in the preview's floating toolbar
      // (below the TV), not the header.
      onStopPreview={overrideActive ? undefined : preview.handlers.stop}
      onReloadPreview={overrideActive ? undefined : preview.handlers.reload}
      onRestartPreview={overrideActive ? undefined : preview.handlers.restart}
      previewMenuEnabled={!overrideActive && preview.state.status === 'ready'}
      controllerUrls={overrideActive ? [] : preview.session.controllerUrls}
      controllerOverride={overrides?.controllerOverride}
      controllerPanelOverride={overrides?.controllerPanelOverride}
      // crucible#pairing — pass previewMode/pairing through for BOTH
      // local and CLOUD paired sessions (a cloud paired preview now
      // carries them). A classic session (local or cloud) has neither, so
      // the PairingPanel stays hidden. Only the override/non-ready cases
      // suppress them.
      pairing={
        overrideActive || preview.state.status !== 'ready'
          ? undefined
          : preview.state.session.pairing
      }
      previewMode={
        overrideActive || preview.state.status !== 'ready'
          ? undefined
          : preview.state.session.previewMode
      }
      onAddPhone={
        !overrideActive && preview.derived.phoneControlsEnabled
          ? () => {
              // Preview lets you add phones up to the overall hard cap
              // (HARD_PREVIEW_PHONE_CAP), independent of the plan's
              // player-count policy — no per-game cap control. Cloud
              // sessions support multiple controllers (each pane gets a
              // distinct same-origin testUserId), so the affordance is
              // wired for cloud too — only paired sessions are excluded.
              void preview.session.addPhone({
                maxControllerSlots: HARD_PREVIEW_PHONE_CAP,
              });
            }
          : undefined
      }
      onRemovePhone={
        !overrideActive && preview.derived.phoneControlsEnabled
          ? () => {
              void preview.session.removePhone();
            }
          : undefined
      }
      onRemovePhoneAt={
        !overrideActive && preview.derived.phoneControlsEnabled
          ? (index) => preview.session.removePhoneAt(index)
          : undefined
      }
      maxControllerSlots={HARD_PREVIEW_PHONE_CAP}
      playerCountLabel={preview.derived.controllerPolicy.label}
      playerCountSource={preview.derived.controllerPolicy.playerCountSource}
      diagnostics={preview.derived.diagnostics}
      loading={overrideActive ? false : preview.derived.launchButtonLoading}
      loadingLabel={overrideActive ? undefined : preview.derived.devLoopState.label}
      loadingDetail={overrideActive ? undefined : preview.derived.devLoopState.detail}
      reloadKey={preview.session.reloadKey}
      onRetryPreview={overrideActive ? undefined : preview.handlers.retryFromOverlay}
      error={
        overrideActive
          ? null
          : preview.state.status === 'error' &&
              preview.state.error.code !== PREVIEW_PHASE_GATE_ERROR_CODE
            ? preview.state.error
            : null
      }
      onSendErrorToChat={
        overrideActive
          ? undefined
          : (err) => {
              const code = err.code ?? 'CRUCIBLE-UNKNOWN';
              const text =
                `Preview failed to start: ${code}. ${err.message ?? ''}`.trim() +
                '\n\nPlease run /diagnose and fix the dev server.';
              chatDispatch({ type: 'user/type', text });
              chatDispatch({ type: 'user/send' });
              preview.handlers.reset();
            }
      }
      onDismissError={overrideActive ? undefined : () => preview.handlers.reset()}
    />
  );
}
